import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Briefcase, MessageCircle, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface NewsItem {
  id: string;
  text_en: string;
  text_ar: string;
  category: string;
  is_active: boolean;
  display_order: number;
}

interface NewsTickerProps {
  items: { id: string; text: string; textAr: string }[];
  icon: LucideIcon;
  label: string;
  labelAr: string;
  variant?: "primary" | "secondary" | "accent";
  className?: string;
}

const variantStyles = {
  primary: "bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-primary/20",
  secondary: "bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-blue-500/10 border-blue-500/20",
  accent: "bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-purple-500/10 border-purple-500/20",
};

const iconStyles = {
  primary: "bg-primary text-primary-foreground",
  secondary: "bg-blue-500 text-white",
  accent: "bg-purple-500 text-white",
};

export const NewsTicker = ({ 
  items, 
  icon: Icon, 
  label, 
  labelAr, 
  variant = "primary",
  className 
}: NewsTickerProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  if (items.length === 0) return null;

  // Duplicate items to create seamless loop
  const duplicatedItems = [...items, ...items, ...items];

  return (
    <div className={cn(
      "relative overflow-hidden border-y py-2",
      variantStyles[variant],
      className
    )}>
      {/* Label Badge */}
      <div className={cn(
        "absolute z-10 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg",
        iconStyles[variant],
        isRTL ? "right-4" : "left-4"
      )}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-bold whitespace-nowrap">
          {isRTL ? labelAr : label}
        </span>
      </div>

      {/* Ticker Content */}
      <div className={cn(
        "flex items-center",
        isRTL ? "pr-44" : "pl-44"
      )}>
        <div 
          className={cn(
            "flex items-center gap-8 whitespace-nowrap",
            isRTL ? "animate-scroll-rtl" : "animate-scroll-ltr"
          )}
        >
          {duplicatedItems.map((item, index) => (
            <div key={`${item.id}-${index}`} className="flex items-center gap-2">
              <span className="text-primary">●</span>
              <span className="text-sm font-medium text-foreground/80">
                {isRTL ? item.textAr : item.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Hook to fetch news items
const useNewsItems = (category: string) => {
  return useQuery({
    queryKey: ["news-ticker", category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_ticker_items")
        .select("*")
        .eq("category", category)
        .eq("is_active", true)
        .order("display_order");
      
      if (error) throw error;
      return (data as NewsItem[]).map(item => ({
        id: item.id,
        text: item.text_en,
        textAr: item.text_ar,
      }));
    },
  });
};

// Pre-configured tickers for different sections
export const EventsNewsTicker = () => {
  const { data: items = [] } = useNewsItems("events");

  return (
    <NewsTicker
      items={items}
      icon={Calendar}
      label="Events & Exhibitions"
      labelAr="الفعاليات والمعارض"
      variant="primary"
    />
  );
};

export const CoursesJobsTicker = () => {
  const { data: items = [] } = useNewsItems("courses");

  return (
    <NewsTicker
      items={items}
      icon={Briefcase}
      label="Courses & Jobs"
      labelAr="الدورات والوظائف"
      variant="secondary"
    />
  );
};

export const SocialUpdatesTicker = () => {
  const { data: items = [] } = useNewsItems("social");

  return (
    <NewsTicker
      items={items}
      icon={MessageCircle}
      label="Social & X Spaces"
      labelAr="التواصل ومساحات X"
      variant="accent"
    />
  );
};
